import React, { useState } from 'react';
import { withSitecoreContext, isEditorActive } from '@sitecore-jss/sitecore-jss-react';
import { TContentHubModelDetailSpecificationsTableProps, TSpecGroup } from './types/index.types';
import { ContentHubModelDetailSpecificationsTableModel } from './models/index.model';
import SpecificationTable from './components/specificationTable/index';
import { textContains } from '../../utils/regex.utils';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';

const ProductModelSpecifications = (props: TContentHubModelDetailSpecificationsTableProps): JSX.Element | null => {
  const apiResponse = isEmptyResponse(props);
  const model = new ContentHubModelDetailSpecificationsTableModel(props);
  const [searchTerm, setSearchTerm] = useState('');
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  const getCategoryRows = (): Array<TSpecGroup> => {
    if (searchTerm) {
      const newCategoryRows: Array<TSpecGroup> = [];

      model.categoryRows.forEach((category) => {
        const newCategory: TSpecGroup = {
          group: '',
          items: [],
        };
        const doesGroupHasSearchTerm = textContains(category.group, searchTerm)      
        const doesAnyItemHasSearchTerm = category.items.find((item) => textContains(item.key, searchTerm));
        
        if (doesGroupHasSearchTerm || doesAnyItemHasSearchTerm) {
          newCategory.group = category.group;
          newCategory.items = category.items;
          newCategoryRows.push(newCategory);
        }
      });
      return newCategoryRows;
    }
    return model.categoryRows;
  };

  const getMrpRows = () => {
    if (searchTerm && !textContains(model.mrpRow?.label || '', searchTerm)) {
      return { ...model.mrpRow, hideRow: true };
    }
    return model.mrpRow;
  };

  const passProps = {
    colHeadings: model.colHeadings,
    originalMrpRow: model.mrpRow,
    originalCategoryRows: model.categoryRows,
    mrpRow: getMrpRows(),
    eyebrow: model.eyebrow,
    title: model.title,
    button1: model.button1,
    contextItemUrl: model.contextItemUrl,
    categoryRows: getCategoryRows(),
    searchTerm: searchTerm,
    setSearchTerm: setSearchTerm,
    convertToExcelLink: model.convertToExcelLink,
    iconsList: model.iconsList,
    backgroundColor: model.backgroundColor,
    backgroundColorClass: model.backgroundColorClass,
    notes: model.notes
  };

  return (
    <AnalyticsVisibleEvent
      rendering={props?.rendering}
      title={model.title?.value}
      template="Specs table"
      className="model-specifications-table-analytics-wrapper"
    >
      <SpecificationTable {...passProps} />
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(ProductModelSpecifications);
